import type { HelpArticleCategoryId } from '@kangaroo/shared';
import { HELP_ARTICLE_CATEGORIES } from '@kangaroo/shared';

export type KnowledgeBaseCategoryId = HelpArticleCategoryId;

export const KNOWLEDGE_BASE_CATEGORIES = HELP_ARTICLE_CATEGORIES;

export type KnowledgeBaseArticle = {
  id: string;
  slug: string;
  categoryId: KnowledgeBaseCategoryId;
  title: string;
  description: string;
  /** Loom share URL id — omit until the walkthrough is recorded. */
  loomId?: string;
  bullets?: string[];
};

export const KNOWLEDGE_BASE_ARTICLES: KnowledgeBaseArticle[] = [
  {
    id: 'welcome',
    slug: 'welcome',
    categoryId: 'getting-started',
    title: 'Welcome to Paddiq',
    description: 'A quick tour of the workspace and how teams use Paddiq day to day.',
    loomId: '05aee2e1292f4a2780f78d99709f8131',
    bullets: [
      'How the sidebar is organized',
      'Where drivers, garage, sessions, and circuits live',
      'What to set up first on a new team',
    ],
  },
  {
    id: 'workspace-setup',
    slug: 'workspace-setup',
    categoryId: 'getting-started',
    title: 'Setting up your workspace',
    description: 'Team creation, roles, and the basics before your first session upload.',
    loomId: '0836bd4c1a324ad091aa5890c9513e37',
  },
  {
    id: 'first-session',
    slug: 'first-session',
    categoryId: 'getting-started',
    title: 'Your first session',
    description: 'Getting telemetry into the app and opening it on the map.',
    loomId: '11b69b32e35d4a22adeb2a51f6234c68',
  },
  {
    id: 'team-management',
    slug: 'team-management',
    categoryId: 'team',
    title: 'Managing your team roster',
    description: 'Add drivers, parents, coaches, and mechanics; link family accounts.',
    bullets: [
      'Invite staff vs create driver logins',
      'Parent and family linking',
      'Team code for access requests',
    ],
  },
  {
    id: 'garage-karts',
    slug: 'garage-karts',
    categoryId: 'garage',
    title: 'Garage — karts and custom setup fields',
    description:
      'Add vehicles, use free-text dropdown values, and track custom attributes like axle or gearing notes.',
    bullets: [
      'Class, engine, and chassis fields accept custom values',
      'Custom attributes (number, text, true/false) save with setup history',
      'Assign karts to drivers for session context',
    ],
  },
  {
    id: 'upload-mychron',
    slug: 'upload-mychron',
    categoryId: 'sessions',
    title: 'Upload MyChron sessions on the web',
    description:
      'Use the four-step Sessions importer to inspect a file or batch, match circuits, assign context, and open finished runs.',
    bullets: [
      'Choose HRZ, HRK, XRZ, XRK, DRK, or exported JSON files without uploading immediately',
      'Confirm a circuit for each file by comparing the saved shape with the recorded lap',
      'Apply one driver and vehicle to the batch, then follow upload and lap-slicing progress',
      'Open each finished session for Raw laps, circuit-assigned laps when available, Map View, and Traces View',
    ],
  },
  {
    id: 'upload-gpx',
    slug: 'upload-gpx',
    categoryId: 'sessions',
    title: 'Upload GPX tracks',
    description:
      'Select Upload, then use “Uploading GPX, CSV, or another file?” — laps are detected from the GPS trace.',
    bullets: [
      'Good for getting a session on the map without a MyChron download',
      'No RPM, brake, or temp channels — GPS and derived lap times only',
      'Sector splits and deep telemetry charts need a native MyChron file',
    ],
  },
  {
    id: 'sessions-review',
    slug: 'sessions-review',
    categoryId: 'sessions',
    title: 'Reviewing laps and comparing sessions',
    description: 'Pick sessions, switch lap timing modes, and compare traces on the map.',
    bullets: [
      'MyChron timing vs circuit-resliced laps',
      'Compare multiple laps or sessions',
      'Overlay the driver’s fastest valid recorded lap for the exact circuit configuration',
      'Session notes, footage alignment, and context panels',
    ],
  },
  {
    id: 'fastest-recorded-lap',
    slug: 'fastest-recorded-lap',
    categoryId: 'sessions',
    title: 'Use the fastest recorded lap reference',
    description:
      'Compare the current lap with the driver’s fastest complete valid lap for the exact circuit configuration.',
    bullets: [
      'Show the dashed blue historical reference on session and telemetry maps',
      'Confirm the source lap, recorded date, and exact saved circuit configuration',
      'Resolve missing driver, circuit, lap-validity, or authorization prerequisites',
    ],
  },
  {
    id: 'circuits-library',
    slug: 'circuits-library',
    categoryId: 'circuits',
    title: 'Circuits and track matching',
    description:
      'Browse the circuit library, review discoveries from uploads, and keep track names tidy.',
    bullets: [
      'New tracks surface after session uploads',
      'Draft vs published circuit records',
      'Track enrichment improves lap and map quality',
    ],
  },
  {
    id: 'mobile-sync',
    slug: 'mobile-sync',
    categoryId: 'mobile',
    title: 'Mobile MyChron sync',
    description:
      'How drivers use the mobile app to scan the logger and sync data to the team workspace.',
    bullets: [
      'Driver login and Wi‑Fi connection to the MyChron',
      'Assign imports to the right session context',
      'Data lands in the same library coaches see on web',
    ],
  },
];

export function loomEmbedUrl(loomId: string) {
  return `https://www.loom.com/embed/${loomId}`;
}

export function getKnowledgeBaseCategory(categoryId: KnowledgeBaseCategoryId) {
  return KNOWLEDGE_BASE_CATEGORIES.find((category) => category.id === categoryId) ?? null;
}

export function getKnowledgeBaseArticle(slug: string | null | undefined) {
  if (!slug) {
    return null;
  }

  return KNOWLEDGE_BASE_ARTICLES.find((article) => article.slug === slug) ?? null;
}

export function getKnowledgeBaseArticlesByCategory(categoryId: KnowledgeBaseCategoryId) {
  return KNOWLEDGE_BASE_ARTICLES.filter((article) => article.categoryId === categoryId);
}

export function getDefaultKnowledgeBaseArticle() {
  return KNOWLEDGE_BASE_ARTICLES[0] ?? null;
}

export function countKnowledgeBaseVideos() {
  return KNOWLEDGE_BASE_ARTICLES.filter((article) => Boolean(article.loomId)).length;
}
